import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useVerifyMagicLink } from '../../hooks/api/useAuth';

export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verifyMagicLink = useVerifyMagicLink();

  useEffect(() => {
    const token = searchParams.get('token');

    // Check if we've already verified this token
    const verifiedToken = sessionStorage.getItem('verified_token');
    if (verifiedToken === token && token) {
      console.log('Token already verified, redirecting...');
      // Get stored redirect URL or default to AI dashboard
      const storedRedirectUrl = sessionStorage.getItem('auth_redirect_url') || '/ai';
      sessionStorage.removeItem('verified_token');
      sessionStorage.removeItem('auth_redirect_url');
      navigate(storedRedirectUrl, { replace: true });
      return;
    }

    if (!token) {
      console.error('Missing token');
      // Store error in sessionStorage so it survives redirect
      sessionStorage.setItem('auth_error', 'Invalid magic link - missing token');
      navigate('/', { replace: true });
      return;
    }

    // Get nonce from localStorage (stored when magic link was requested)
    // Safari's ITP may clear localStorage, so provide helpful error message
    let nonce: string | null = null;
    try {
      nonce = localStorage.getItem('auth_nonce');
    } catch (error) {
      console.error('Failed to access localStorage (Safari private mode?):', error);
      sessionStorage.setItem(
        'auth_error',
        'Cannot access browser storage. Please disable private browsing mode or enable storage for this site.',
      );
      navigate('/', { replace: true });
      return;
    }

    if (!nonce) {
      console.error('Missing nonce in localStorage - may have been cleared by Safari ITP');
      // Store error in sessionStorage so it survives redirect
      sessionStorage.setItem(
        'auth_error',
        'Magic link expired or browser storage was cleared. Please request a new login link.',
      );
      navigate('/', { replace: true });
      return;
    }

    // Mark token as being verified to prevent re-execution
    sessionStorage.setItem('verified_token', token);

    // Verify the magic link
    verifyMagicLink.mutate(
      { token, nonce },
      {
        onSuccess: (data) => {
          // Clean up the nonce from localStorage (BUGFIX: was incorrectly using sessionStorage)
          localStorage.removeItem('auth_nonce');

          // Store redirect URL and redirect
          const redirectUrl = data.redirectUrl || '/ai';
          sessionStorage.setItem('auth_redirect_url', redirectUrl);
          sessionStorage.removeItem('verified_token'); // Clear verification flag
          navigate(redirectUrl, { replace: true });
        },
        onError: (error) => {
          console.error('Magic link verification failed:', error);
          // Clear verification flag on error
          sessionStorage.removeItem('verified_token');
          // Store error in sessionStorage so it survives redirect
          sessionStorage.setItem('auth_error', `Magic link verification failed: ${error.message}`);
          navigate('/', { replace: true });
        },
      },
    );
  }, [searchParams, navigate, verifyMagicLink]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <h2 className="text-xl font-semibold">Verifying your login...</h2>
        <p className="text-muted-foreground">Please wait while we authenticate you.</p>
      </div>
    </div>
  );
}
